import { useCallback, useEffect, useMemo } from "react";
import { useRefreshToken } from "./useRefreshToken";
import { useMe } from "./useMe";
import { mapMeUser } from "../utils/map";
import { UserInfo } from "../types";
import { removeLastUrlPath } from "../services/cookies";
import { useAuth } from "../store/auth";

export const useBoot = () => {
  const { data: refreshTokenData, isLoading: isLoadingToken } =
    useRefreshToken();

  const { data: userMe, isLoading: isLoadingUserInfo } =
    useMe(!!refreshTokenData);

  const { token: newAccessToken } = refreshTokenData || {};

  const userInfo = useMemo((): UserInfo | undefined => {
    if (!userMe || !newAccessToken) return undefined;

    return mapMeUser(userMe, newAccessToken);
  }, [userMe, newAccessToken]);

  const hasPermission = useCallback(
    (permission: string): boolean => {
      if (!userInfo) {
        return false;
      }

      return (
        permission === "all" ||
        userInfo.profile.permissions.includes(permission)
      );
    },
    [userInfo],
  );

  const isLoading = useMemo(() => {
    return isLoadingToken || isLoadingUserInfo;
  }, [isLoadingToken, isLoadingUserInfo]);

  const isAuthenticated = useMemo(() => {
    return !!userInfo;
  }, [userInfo]);

  useEffect(() => {
    if (userInfo) {
      useAuth.getState().updateUserInfo(userInfo);
    }
  }, [userInfo]);

  useEffect(() => {
    if (isAuthenticated) {
      removeLastUrlPath();
    }
  }, [isAuthenticated]);

  return {
    hasPermission,
    isAuthenticated,
    userInfo,
    isLoading,
  };
};
